import APIRequester from "src/api/APIRequester"
import LoginHelper from "src/components/login/LoginHelper"
import AuthenticationError from "src/validation/exception_messages/AuthenticationError"
import RedirectToLoginException from "src/validation/exception_messages/RedirectToLoginException"
import FeatureProvider from "src/features/FeatureProvider"
import FeatureFlagIds from "src/features/FeatureFlagIds"
import FeatureFlag from "src/features/FeatureFlag"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import posthog from "src/consent/posthog"


class ApiHandler {
    baseURL: string 
    comparableHomesURL: string 
    valuateURL: string 
    findAddressURL: string 
    suggestAddressURL: string 
    getAddressURL: string 
    getUserInfoURL: string 
    getAddressImageURL: string 
    saveValuationURL: string
    updateSavedValuationValueURL: string
    listValuationsURL: string
    deleteValuationURL: string
    createCheckoutURL: string
    createReportCheckoutURL: string
    createSubscriptionCheckoutURL: string
    createBillingPortalSessionURL: string
    confirmReportPaymentURL: string
    findAreaURL: string
    suggestAreaURL: string
    valuationUsageURL: string
    globalUsageURL: string
    reportPriceTierURL: string
    areaSignalsURL: string
    nearbyAgentsURL: string
    getAgentLogoURL: string
    agentEnquiryURL: string
    inPersonValuationLeadURL: string
    sendVerificationCodeURL: string
    verifyPhoneURL: string
    refinedValuationResearchURL: string
    reportPurchaseURL: string
    listRefinedValuationsURL: string
    deleteRefinedValuationURL: string
    updateRefinedValuationURL: string
    estimateValuationURL: string
    completeAnalysisURL: string
    previewAnalysisReportURL: string
    reopenAnalysisURL: string
    deliverReportURL: string
    searchComparablesURL: string
    getListingPhotosURL: string
    getReportPhotosURL: string
    getReportAddressImageURL: string
    rateListingConditionURL: string
    compareHomesURL: string
    findListingURL: string
    consumeListingURL: string
    getRefinedValuationURL: string
    listReportsURL: string
    getReportURL: string
    getReportPreviewURL: string
    apiRequester: APIRequester
    loginHelper: LoginHelper
    stateHandler: EventAdaptingStateHandler
    requestCache: Map<string, any>
    featureProvider: FeatureProvider
    private imageQueue: Promise<any> = Promise.resolve()
    private inFlightImages: Map<string, Promise<any>> = new Map()
    private agentLogoQueue: Promise<any> = Promise.resolve()
    private inFlightAgentLogos: Map<string, Promise<any>> = new Map()

    constructor(apiRequester: APIRequester, baseURL: string, loginHelper: LoginHelper, stateHandler: EventAdaptingStateHandler, featureProvider: FeatureProvider) {
        this.apiRequester = apiRequester
        this.baseURL = baseURL
        this.comparableHomesURL = this.baseURL + "/comparable_homes"
        this.valuateURL = this.baseURL + "/valuate"
        this.findAddressURL = this.baseURL + "/find_address"
        this.suggestAddressURL = this.baseURL + "/suggest_address"
        this.getUserInfoURL = this.baseURL + "/get_user_info"
        this.getAddressImageURL = this.baseURL + "/get_address_image"
        this.getAddressURL = this.baseURL + "/get_address"
        this.saveValuationURL = this.baseURL + "/save_valuation"
        this.updateSavedValuationValueURL = this.baseURL + "/update_saved_valuation_value"
        this.listValuationsURL = this.baseURL + "/list_valuations"
        this.deleteValuationURL = this.baseURL + "/delete_valuation"
        this.createCheckoutURL = this.baseURL + "/create_checkout"
        this.createReportCheckoutURL = this.baseURL + "/create_report_checkout"
        this.createSubscriptionCheckoutURL = this.baseURL + "/create_subscription_checkout"
        this.createBillingPortalSessionURL = this.baseURL + "/create_billing_portal_session"
        this.confirmReportPaymentURL = this.baseURL + "/confirm_report_payment"
        this.findAreaURL = this.baseURL + "/find_area"
        this.suggestAreaURL = this.baseURL + "/suggest_area"
        this.valuationUsageURL = this.baseURL + "/valuation_usage"
        this.globalUsageURL = this.baseURL + "/global_usage"
        this.reportPriceTierURL = this.baseURL + "/report_price_tier"
        this.areaSignalsURL = this.baseURL + "/area_signals"
        this.nearbyAgentsURL = this.baseURL + "/nearby_agents"
        this.getAgentLogoURL = this.baseURL + "/get_agent_logo"
        this.agentEnquiryURL = this.baseURL + "/agent_enquiry"
        this.inPersonValuationLeadURL = this.baseURL + "/in_person_valuation_lead"
        this.sendVerificationCodeURL = this.baseURL + "/send_verification_code"
        this.verifyPhoneURL = this.baseURL + "/verify_phone"
        this.refinedValuationResearchURL = this.baseURL + "/refined_valuation_research"
        this.reportPurchaseURL = this.baseURL + "/report_purchase"
        this.listRefinedValuationsURL = this.baseURL + "/list_refined_valuations"
        this.getRefinedValuationURL = this.baseURL + "/get_refined_valuation"
        this.listReportsURL = this.baseURL + "/list_reports"
        this.getReportURL = this.baseURL + "/get_report"
        this.getReportPreviewURL = this.baseURL + "/get_report_preview"
        this.deleteRefinedValuationURL = this.baseURL + "/delete_refined_valuation"
        this.updateRefinedValuationURL = this.baseURL + "/update_refined_valuation"
        this.estimateValuationURL = this.baseURL + "/estimate_valuation"
        this.completeAnalysisURL = this.baseURL + "/complete_analysis"
        this.previewAnalysisReportURL = this.baseURL + "/preview_analysis_report"
        this.reopenAnalysisURL = this.baseURL + "/reopen_analysis"
        this.deliverReportURL = this.baseURL + "/deliver_report"
        this.searchComparablesURL = this.baseURL + "/search_comparables"
        this.getListingPhotosURL = this.baseURL + "/get_listing_photos"
        this.getReportPhotosURL = this.baseURL + "/get_report_photos"
        this.getReportAddressImageURL = this.baseURL + "/get_report_address_image"
        this.rateListingConditionURL = this.baseURL + "/rate_listing_condition"
        this.compareHomesURL = this.baseURL + "/compare_homes"
        this.findListingURL = this.baseURL + "/find_listing"
        this.consumeListingURL = this.baseURL + "/consume_listing"
        this.loginHelper = loginHelper;
        this.stateHandler = stateHandler;
        this.requestCache = new Map();
        this.featureProvider = featureProvider;
    }

    clearCache() {
        this.requestCache.clear();
    }

    getCacheKey(url: string, input: any): string {
        return `${url}-${JSON.stringify(input)}`;
    }

    async getAddress(input: { geo_id: string } | { unique_id: string } | { unique_location_id: string }) {
        return await this.postOrLogin(this.getAddressURL, input)
    }

    async getComparableHomes(input: { geo_id: string } | { unique_id: string } | { address: string }) {
        return await this.postOrLogin(this.comparableHomesURL, input)
    }

    async getValuation(input: any)  {
        return await this.postOrLogin(this.valuateURL, input)
    }

    async findAddress(input: any) {
        return await this.postOrLogin(this.findAddressURL, input)
    }

    async suggestAddress(input: any) {
        return await this.postOrLogin(this.suggestAddressURL, input)
    }

    async getUserInfo() {
        return await this.postOrLogin(this.getUserInfoURL, {})
    }

    async refreshUserInfo() {
        this.requestCache.delete(this.getCacheKey(this.getUserInfoURL, {}))
        return await this.postOrLogin(this.getUserInfoURL, {}, true)
    }

    /** Buying a report: start with an email, check where it has got to, send the details once paid. */
    async reportPurchase(input: any) {
        return await this.postOrLogin(this.reportPurchaseURL, input, true, false)
    }

    async submitRefinedValuationResearch(input: any) {
        return await this.postOrLogin(this.refinedValuationResearchURL, input, true)
    }

    /** Fixed dimensions for card thumbnails so cache keys stay consistent. */
    static readonly THUMBNAIL_WIDTH = 160
    static readonly THUMBNAIL_HEIGHT = 120
    /** Fixed dimensions for the hero street-view on the valuation page. */
    static readonly HERO_WIDTH = 640
    static readonly HERO_HEIGHT = 360
    /**
     * The street view a report shows of a home with no photographs of its own.
     *
     * Large because a report frames it big and a reader looks at it: the thumbnail size stretched
     * into a report card is mush. Over 640 on a side, the Streetview api serves scale=2, so this
     * is one billable request returning 1024x768 real pixels. One size for every frame in the
     * report rather than one per frame — they all cover rather than stretch, so the largest that
     * any of them needs is the only one worth minting.
     */
    static readonly REPORT_IMAGE_WIDTH = 1024
    static readonly REPORT_IMAGE_HEIGHT = 768

    /**
     * Every distinct (width, height) that reaches `/get_address_image` creates a
     * separate S3 cache entry and a separate Streetview billable request. To
     * keep costs bounded we only permit a small allowlist of sizes; any other
     * pair is a coding bug and must fail loudly rather than quietly minting a
     * new cached size.
     */
    private static readonly ALLOWED_IMAGE_DIMENSIONS: ReadonlyArray<{ width: number; height: number }> = [
        { width: ApiHandler.THUMBNAIL_WIDTH, height: ApiHandler.THUMBNAIL_HEIGHT },
        { width: ApiHandler.HERO_WIDTH, height: ApiHandler.HERO_HEIGHT },
        { width: ApiHandler.REPORT_IMAGE_WIDTH, height: ApiHandler.REPORT_IMAGE_HEIGHT },
    ]

    /** Delay between consecutive /get_address_image requests, to avoid stampeding cold lambdas. */
    private static readonly IMAGE_REQUEST_GAP_MS = 100

    async getAddressImage(input: any) {
        const isAllowedSize = ApiHandler.ALLOWED_IMAGE_DIMENSIONS.some(
            (d) => d.width === input.width && d.height === input.height,
        )
        if (!isAllowedSize) {
            const allowed = ApiHandler.ALLOWED_IMAGE_DIMENSIONS.map((d) => `${d.width}x${d.height}`).join(", ")
            throw new Error(
                `getAddressImage refused: dimensions ${input.width}x${input.height} are not in the allowlist (${allowed}). ` +
                `Every unique size mints a new S3 cache entry AND a new Streetview billable request. ` +
                `If you genuinely need a new size, add it to ApiHandler.ALLOWED_IMAGE_DIMENSIONS and its matching constant.`,
            )
        }

        const cacheKey = `img_${input.location_id}_${input.width}_${input.height}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) return JSON.parse(cached);
        } catch {}

        const inFlight = this.inFlightImages.get(cacheKey);
        if (inFlight) return inFlight;

        const next = this.imageQueue.then(async () => {
            try {
                const cached = sessionStorage.getItem(cacheKey);
                if (cached) return JSON.parse(cached);
            } catch {}

            const response = await this.postOrLogin(this.getAddressImageURL, input);

            if (response?.raw_image && response.valid_image === true) {
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(response));
                } catch {}
            }

            return response;
        });

        this.inFlightImages.set(cacheKey, next);
        this.imageQueue = next
            .catch(() => {})
            .then(() => new Promise(r => setTimeout(r, ApiHandler.IMAGE_REQUEST_GAP_MS)));
        next.finally(() => {
            if (this.inFlightImages.get(cacheKey) === next) {
                this.inFlightImages.delete(cacheKey);
            }
        });

        return next;
    }

    /**
     * Get a cached address thumbnail with fixed size for use in cards.
     * Always uses the same dimensions so backend and client caching stay consistent.
     */
    async getAddressThumbnail(locationId: string, locationString: string) {
        return await this.getAddressImage({
            location_id: locationId,
            location_string: locationString,
            width: ApiHandler.THUMBNAIL_WIDTH,
            height: ApiHandler.THUMBNAIL_HEIGHT,
            return_saved: true,
        })
    }

    /**
     * Get the valuation-page hero street-view image at a fixed size.
     * Always uses the same dimensions so backend and client caching stay consistent.
     */
    async getAddressHeroImage(locationId: string, locationString: string) {
        return await this.getAddressImage({
            location_id: locationId,
            location_string: locationString,
            width: ApiHandler.HERO_WIDTH,
            height: ApiHandler.HERO_HEIGHT,
            return_saved: true,
        })
    }

    /**
     * The street view at report resolution, generated once and cached under its own key.
     *
     * Asked for at the same size every time so the cache is hit rather than a second size minted:
     * the first report to want a home pays for the image and every later one is served the saved
     * copy.
     */
    async getAddressReportImage(locationId: string, locationString: string) {
        return await this.getAddressImage({
            location_id: locationId,
            location_string: locationString,
            width: ApiHandler.REPORT_IMAGE_WIDTH,
            height: ApiHandler.REPORT_IMAGE_HEIGHT,
            return_saved: true,
        })
    }

    async saveValuation(input: any) {
        return await this.postOrLogin(this.saveValuationURL, input)
    }

    async updateSavedValuationValue(input: any) {
        return await this.postOrLogin(this.updateSavedValuationValueURL, input)
    }

    async listRefinedValuations(input: { state?: string; user_id?: string; unique_id?: string; limit?: number; include_archived?: boolean }) {
        return await this.postOrLogin(this.listRefinedValuationsURL, input, true, false)
    }

    async deleteRefinedValuation(input: { id: string }) {
        return await this.postOrLogin(this.deleteRefinedValuationURL, input, true, false)
    }

    async searchComparables(input: {
        unique_id: string
        limit?: number
        order_by?: string
        radius_metres?: number | null
        sold_within_months?: number | null
        minimum_beds?: number
        maximum_beds?: number
        minimum_baths?: number
        maximum_baths?: number
        minimum_sqm?: number
        maximum_sqm?: number
        minimum_ber_rating?: string
        maximum_ber_rating?: string
        property_types?: string[]
        with_photos_only?: boolean
    }) {
        return await this.postOrLogin(this.searchComparablesURL, input, true, false)
    }

    /** Photos are cached: the same ids return the same bytes, and they are large. */
    async getListingPhotos(input: { photo_ids: string[] }) {
        return await this.postOrLogin(this.getListingPhotosURL, input, true)
    }

    /**
     * The photographs of one report, for whoever may read it. Only ids the report holds are
     * answered, so this is the one a homeowner's report page uses: getListingPhotos is the team's.
     */
    async getReportPhotos(input: { id: string, photo_ids: string[] }) {
        return await this.postOrLogin(this.getReportPhotosURL, input, true)
    }

    /**
     * The street view of one home in a report, for whoever may read the report. The server
     * checks the report and that the home is one it shows, and chooses the address and the size,
     * so this is not held to the limits of getAddressImage and is not queued behind it: a page of
     * homes asks together, each as it scrolls near.
     */
    async getReportAddressImage(reportId: string, locationId: string) {
        const cacheKey = `report_img_${reportId}_${locationId}`
        try {
            const cached = sessionStorage.getItem(cacheKey)
            if (cached) return JSON.parse(cached)
        } catch {}

        const inFlight = this.inFlightImages.get(cacheKey)
        if (inFlight) return inFlight

        const next = (async () => {
            const response = await this.postOrLogin(
                this.getReportAddressImageURL, { id: reportId, location_id: locationId }, true,
            )
            if (response?.raw_image && response.valid_image === true) {
                try { sessionStorage.setItem(cacheKey, JSON.stringify(response)) } catch {}
            }
            return response
        })()
        this.inFlightImages.set(cacheKey, next)
        next.then(
            () => this.inFlightImages.delete(cacheKey),
            () => this.inFlightImages.delete(cacheKey),
        )
        return next
    }

    /** Never cached: each ask is a paid model call, and a re-ask is meant to be a fresh opinion. */
    async rateListingCondition(input: { photo_ids: string[] }) {
        return await this.postOrLogin(this.rateListingConditionURL, input, true, false)
    }

    /** Never cached: each ask is a paid model call. Sale ids pin the floor area to that sale. */
    async compareHomes(input: {
        subject_unique_id: string
        candidate_unique_id: string
        subject_sale_id?: string
        candidate_sale_id?: string
    }) {
        return await this.postOrLogin(this.compareHomesURL, input, true, false)
    }

    /**
     * Never cached: each ask is a Google search and the page fetches behind it, and where the server
     * has auto-consume on it also writes.
     */
    async findListing(input: { unique_id: string; sale_id: string }) {
        return await this.postOrLogin(this.findListingURL, input, true, false)
    }

    /** Never cached: it writes, and the same url consumed twice is a real second request. */
    async consumeListing(input: {
        url: string; unique_id: string; sale_id?: string;
        /** Set once an analyst has been shown both addresses and says it is the same home. */
        force?: boolean;
    }) {
        return await this.postOrLogin(this.consumeListingURL, input, true, false)
    }

    async updateRefinedValuationAnalysis(input: { id: string; analysis: any }) {
        return await this.postOrLogin(this.updateRefinedValuationURL, input, true, false)
    }

    async estimateValuation(input: { id: string }) {
        return await this.postOrLogin(this.estimateValuationURL, input, true, false)
    }

    async completeAnalysis(input: { id: string }) {
        return await this.postOrLogin(this.completeAnalysisURL, input, true, false)
    }

    async reopenAnalysis(input: { id: string }) {
        return await this.postOrLogin(this.reopenAnalysisURL, input, true, false)
    }

    async deliverReport(input: {
        id: string; suppress_email?: boolean; override_recipient?: string
    }) {
        return await this.postOrLogin(this.deliverReportURL, input, true, false)
    }

    async archiveRefinedValuation(input: { id: string; archived: boolean }) {
        return await this.postOrLogin(this.updateRefinedValuationURL, input, true, false)
    }

    /** Reports the caller may see, as previews. Null when nobody is logged in. */
    async listReports(input: { limit?: number; offset?: number; user_id?: string; scope?: "own" | "all" }) {
        return await this.postOrLogin(this.listReportsURL, input, true, false)
    }

    /**
     * One report, in full. Unlike the other calls this lets a 401 or 403 reach the caller rather
     * than turning it into null: a report that is the caller's but not unlocked is a 403 carrying
     * report_locked, and the page has to tell that from being signed out.
     */
    async getReport(input: { id: string }) {
        try {
            const tokenHeader = await this.getTokenHeaderOrLogin()
            return await this.apiRequester.post(this.getReportURL, input, tokenHeader)
        } catch (error) {
            this.captureApiError(this.getReportURL, error)
            throw error
        }
    }

    /** What a report's owner may see before they have the report: their own inputs and the work behind it. */
    async getReportPreview(input: { id: string }) {
        return await this.postOrLogin(this.getReportPreviewURL, input, true, false)
    }

    /** The preview its owner would be shown of the report this analysis comes to. Writes nothing. */
    async previewAnalysisReport(input: { id: string }) {
        return await this.postOrLogin(this.previewAnalysisReportURL, input, true, false)
    }

    async getRefinedValuation(input: { id: string }) {
        return await this.postOrLogin(this.getRefinedValuationURL, input, true, false)
    }

    async listValuations(input: any, preventForceLogin: boolean = false) {
        return await this.postOrLogin(this.listValuationsURL, input, preventForceLogin)
    }

    async deleteValuation(input: any) {
        return await this.postOrLogin(this.deleteValuationURL, input)
    }

    async createCheckout(email?: string) {
        const input = email ? { email } : {}
        const cacheKey = this.getCacheKey(this.createCheckoutURL, input)
        this.requestCache.delete(cacheKey)
        return await this.postOrLogin(this.createCheckoutURL, input, true)
    }

    async createSubscriptionCheckout(returnPath: string) {
        return await this.postOrLogin(this.createSubscriptionCheckoutURL, { return_path: returnPath }, true, false)
    }

    async createBillingPortalSession(returnPath: string) {
        return await this.postOrLogin(this.createBillingPortalSessionURL, { return_path: returnPath }, true, false)
    }

    /** Starts the payment for one report. Never cached: every call is a new payment to complete. */
    async createReportCheckout(input: { id: string, disclaimer_version: string, checkout_token?: string }) {
        return await this.postOrLogin(this.createReportCheckoutURL, input, true, false)
    }

    /** Asks the server to check a payment with Stripe and grant the report if it went through. Never cached. */
    async confirmReportPayment(input: { id: string; payment_intent_id: string; checkout_token?: string }) {
        return await this.postOrLogin(this.confirmReportPaymentURL, input, true, false)
    }

    async findArea(input: { id?: string; text?: string }) {
        return await this.postOrLogin(this.findAreaURL, input)
    }

    async suggestArea(input: { area: string }) {
        return await this.postOrLogin(this.suggestAreaURL, input)
    }

    async claimPayment(paymentIntentId: string) {
        return await this.postOrLogin(this.baseURL + "/claim_payment", { payment_intent_id: paymentIntentId })
    }

    async getReportPriceTier(): Promise<{ display_price_eur: number, charged_price_eur: number }> {
        return await this.postOrLogin(this.reportPriceTierURL, {}, true, false)
    }

    async getGlobalUsage() {
        return await this.apiRequester.post(this.globalUsageURL, {})
    }

    async getAreaSignals(input: { geo_id: string } | { unique_id: string } | { address: string }) {
        return await this.postOrLogin(this.areaSignalsURL, input)
    }

    async getNearbyAgents(input: { geo_id: string } | { unique_id: string }) {
        const idValue = "geo_id" in input ? input.geo_id : input.unique_id
        const sessionCacheKey = `nearby_agents_${idValue}`
        try {
            const cached = sessionStorage.getItem(sessionCacheKey)
            if (cached) return JSON.parse(cached)
        } catch {}

        const requestCacheKey = this.getCacheKey(this.nearbyAgentsURL, input)
        if (this.requestCache.has(requestCacheKey)) {
            return await this.requestCache.get(requestCacheKey)
        }

        const request = this.apiRequester.post(this.nearbyAgentsURL, input)
            .then(response => {
                try {
                    sessionStorage.setItem(sessionCacheKey, JSON.stringify(response))
                } catch {}
                return response
            })
            .catch(error => {
                this.requestCache.delete(requestCacheKey)
                throw error
            })
        this.requestCache.set(requestCacheKey, request)
        return await request
    }

    async getAgentLogo(input: { agent_id: string }) {
        const cacheKey = `agent_logo_${input.agent_id}`
        try {
            const cached = sessionStorage.getItem(cacheKey)
            if (cached) return JSON.parse(cached)
        } catch {}

        const inFlight = this.inFlightAgentLogos.get(cacheKey)
        if (inFlight) return inFlight

        const next = this.agentLogoQueue.then(async () => {
            try {
                const cached = sessionStorage.getItem(cacheKey)
                if (cached) return JSON.parse(cached)
            } catch {}

            const response = await this.apiRequester.post(this.getAgentLogoURL, input)
            if (response?.raw_image && response.valid_image === true) {
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(response))
                } catch {}
            }
            return response
        })

        this.inFlightAgentLogos.set(cacheKey, next)
        this.agentLogoQueue = next
            .catch(() => {})
            .then(() => new Promise(resolve => setTimeout(resolve, 100)))
        next.finally(() => {
            if (this.inFlightAgentLogos.get(cacheKey) === next) {
                this.inFlightAgentLogos.delete(cacheKey)
            }
        })
        return next
    }

    async submitAgentEnquiry(input: any) {
        return await this.apiRequester.post(this.agentEnquiryURL, input)
    }

    async submitInPersonValuationLead(input: any) {
        return await this.apiRequester.post(this.inPersonValuationLeadURL, input)
    }

    async sendVerificationCode(phoneNumber: string) {
        return await this.apiRequester.post(this.sendVerificationCodeURL, { phone_number: phoneNumber })
    }

    async verifyPhone(phoneNumber: string, code: string) {
        return await this.apiRequester.post(this.verifyPhoneURL, { phone_number: phoneNumber, code })
    }

    async getValuationUsage() {
        const cacheKey = this.getCacheKey(this.valuationUsageURL, {})
        this.requestCache.delete(cacheKey)
        return await this.postOrLogin(this.valuationUsageURL, {}, true)
    }

    async postOrLogin(url: string, input: any, preventForceLogin: boolean = false, useCache: boolean = true) {
        const cacheKey = this.getCacheKey(url, input);

        if (useCache && this.requestCache.has(cacheKey)) {
            return this.requestCache.get(cacheKey);
        }

        let response 
        try {
            let tokenHeader = await this.getTokenHeaderOrLogin()
            response = await this.apiRequester.post(url, input, tokenHeader)
            if (response?.metadata?.li == false) {
                try {
                    await this.loginHelper.logout()
                } catch (error) {
                    // Ignore error
                }
                this.stateHandler.set(StateConstants.IS_LOGGED_IN, false)
            }
            
            if (useCache) {
                this.requestCache.set(cacheKey, response);
            }
            
        } catch(error) {
            this.captureApiError(url, error)
            if (error instanceof AuthenticationError) {
                if (preventForceLogin) {
                    return null
                }
                await this.loginHelper.logout()
                this.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                throw new RedirectToLoginException("User is not authenticated, redirecting to login.")
            } else {
                throw error
            }
        }
        if (response?.metadata?.gb) {
            this.stateHandler.set(StateConstants.IS_GOOGLE_BOT, true)
        }
        return response
    }

    private captureApiError(url: string, error: any) {
        const endpoint = url.startsWith(this.baseURL) ? url.slice(this.baseURL.length) : url
        const status = typeof error?.status === "number" ? error.status : undefined
        posthog?.capture("api_error", {
            endpoint,
            status,
            error_type: error?.name ?? "Error",
            error_message: typeof error?.message === "string" ? error.message.slice(0, 200) : undefined,
        })
    }

    async getTokenHeaderOrLogin() {
        let isAuthRequired: FeatureFlag = this.featureProvider.getFeatureFlag(FeatureFlagIds.REQUIRE_AUTHENTICATION);
        let token: any = await this.loginHelper.getValidToken()
        if (token && Object.keys(token).length > 0) {
            return {
                "x-auth-token": token["id_token"],
                "x-auth-access-token": token["access_token"]
            }
        }
        if (!isAuthRequired.isEnabled()) {
            return {}
        }
        throw new AuthenticationError("User is not logged in, no valid authentication token.")
    }
}

export default ApiHandler
